import { useContext, useMemo, useState } from 'react';

// Third-parties.
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import ConditionalRender from "../../../helpers/ConditionalRender";

// Helper.
import Helper from '../../../helpers/Helper';
import { chartOptionsGranularity } from "../../../helpers/chartOptionsIncreasedGranularity";

// Types.
import HostTarget from '../../../types/host/HostTarget';
import ChartSeries from '../../../types/ChartSeries';
import ChartPlotLine from '../../../types/ChartPlotLine';

// Constants.
import {
    DATA_INITIALISING,
    DATA_LOADING,
    DATA_LOADED,
    CHART_COLOURS_STATEMENTS, CHART_DATA_TYPE
} from '../../Constants';
import { VIEW_MODE } from '../../Constants';
import { getConvertedTimeToUTC, highchartsCredits } from "../../../helpers/utils";
import {useLicences} from "../../../context/LicenceContext";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import HostApi from "../../../api/host/HostApi";

function DiskChart(props: {host: HostTarget, plotLines?: ChartPlotLine[] }) {
    const [loadingBasic, setLoadingBasic] = useState<number>(DATA_LOADING);
    const [loadingDetail, setLoadingDetail] = useState<number>(DATA_INITIALISING);

    const [writeSeries, setWriteSeries] = useState<ChartSeries[]>([]);
    const [readWrittenSeries, setReadWrittenSeries] = useState<ChartSeries[]>([]);

    const [basicTimeSeries, setBasicTimeSeries] = useState<number[][]>([]);
    const [timeSeries, setTimeSeries] = useState<number[][]>([]);
    const [mode, setMode] = useState<number>(VIEW_MODE.BASIC);
    const { licences } = useLicences ();
    const timeRangeContext = useContext(TimeRangeContext)

    const timePeak = useMemo(() => {
        return Helper.getPeakValue(basicTimeSeries.map(i => i[1]))
    }, [basicTimeSeries])

    void useMemo(async() => {
        const limit = process.env.REACT_APP_API_LIMIT ? Number(process.env.REACT_APP_API_LIMIT) : undefined;
        const data = await HostApi.getHostStatisticTime(buildApiQuery(timeRangeContext.timeRange), props.host.id, 'ioutilisation', limit);
        setBasicTimeSeries(data.map(item => {
            return [getConvertedTimeToUTC(item), item.avg]
        }));
        setLoadingBasic(DATA_LOADED);
    }, [timeRangeContext, props.host]);

    const basicChart = useMemo(() => {
        const basicChartOptions = chartOptionsGranularity([
            {
                name: 'Average disk I/O',
                type: 'spline',
                minHeight: 3,
                data: basicTimeSeries,
                zIndex: 1,
                yAxis: 1,
            }], timePeak, {
            credits: {enabled: highchartsCredits(licences)},
            timeRangeContext: timeRangeContext,
            tooltip: {
                formatter: function () {
                    return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.PERCENTAGE);
                }
            },
            plotLines: props.plotLines || []
        })
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts} options={basicChartOptions}/>;

    }, [basicTimeSeries, timePeak, timeRangeContext, licences, props.plotLines]);


    async function getReadData() {

        // Set the metrics we're going to retrieve via API calls.
        const metrics: string[] = ['K read', 'K written']

        let seriesData: any[] = [];
        // Get detailed data.
        const limit = process.env.REACT_APP_API_LIMIT ? Number(process.env.REACT_APP_API_LIMIT) : undefined;
        for (let index = 0; index < metrics.length; index++) {
            const metric = metrics[index];
            const data = await HostApi.getHostStatisticTime(buildApiQuery(timeRangeContext.timeRange), props.host.id, metric, limit);
            seriesData.push({
                name: metric,
                color: CHART_COLOURS_STATEMENTS[index],
                type: 'column',
                data: data.map(dat => {
                    return [getConvertedTimeToUTC(dat), dat.avg]
                }),
                zIndex: 1
            });
        }
        setWriteSeries(seriesData)
    }

    async function getWrittenData() {

        // Set the metrics we're going to retrieve via API calls.
        const metrics: string[] = ['reads completed', 'writes completed', 'I/Os currently in progress'];

        let seriesData: any[] = [];
        // Get detailed data.
        for (let index = 0; index < metrics.length; index++) {
            const metric = metrics[index];
            const data = await HostApi.getHostStatisticTime(buildApiQuery(timeRangeContext.timeRange), props.host.id, metric);
            seriesData.push({
                name: metric,
                color: CHART_COLOURS_STATEMENTS[index],
                type: 'column',
                data: data.map(dat => {
                    return [getConvertedTimeToUTC(dat), dat.avg]
                }),
                zIndex: 1
            });
        }
        setReadWrittenSeries(seriesData)
    }


    async function getTimeData() {

        // Set the metrics we're going to retrieve via API calls.
        const metrics: string[] = ['time spent reading (ms)', 'time spent writing (ms)', 'time spent doing I/Os (ms)', 'weighted time spent doing I/Os (ms)'];

        let seriesData: any[] = [];
        // Get detailed data.
        for (let index = 0; index < metrics.length; index++) {
            const metric = metrics[index];
            const data = await HostApi.getHostStatisticTime(buildApiQuery(timeRangeContext.timeRange), props.host.id, metric);
            const type: string = ((metric === 'time spent doing I/Os (ms)' || metric === 'weighted time spent doing I/Os (ms)') ? 'spline' : 'column');
            seriesData.push({
                name: metric,
                color: CHART_COLOURS_STATEMENTS[index],
                type: type,
                data: data.map(dat => {
                    return [getConvertedTimeToUTC(dat), dat.avg]
                }),
                zIndex: 1,
                yAxis: ((type === 'spline') ? 1 : 0)
            });
        }
        // Mark the content as loaded.
        setLoadingDetail(DATA_LOADED);
        setTimeSeries(seriesData)
    }

    const chartOptions = {
        credits: {enabled: highchartsCredits(licences)},
        timeRangeContext: timeRangeContext,
        legend: {
            align: 'left',
            backgroundColor: 'transparent',
            enabled: false,
            floating: false,
            itemMarginTop: 0,
            itemMarginBottom: 2,
            itemStyle: {
                fontSize: '.7rem'
            },
            layout: 'horizontal',
            padding: 0,
            x: 0,
            y: 0,
            verticalAlign: 'top'
        },
        tooltip: {
            formatter: function () {
                return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.GENERIC);
            }
        },
        plotLines: props.plotLines || []
    }

    const readWritesChart = useMemo(() => {
        const detailChartOptions = chartOptionsGranularity(
            writeSeries, 100, chartOptions)
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts} options={detailChartOptions}/>;

    }, [writeSeries, timePeak, timeRangeContext, licences, props.plotLines]);

    const readWrittenChart = useMemo(() => {
        const detailChartOptions = chartOptionsGranularity(
            readWrittenSeries, 100, chartOptions)
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts} options={detailChartOptions}/>;

    }, [readWrittenSeries, timePeak, timeRangeContext, licences, props.plotLines]);

    const timeChart = useMemo(() => {
        const detailChartOptions = chartOptionsGranularity(
            timeSeries, 100, chartOptions)
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts} options={detailChartOptions}/>;

    }, [timeSeries, timePeak, timeRangeContext, licences, props.plotLines]);

    function toggleChart() {

        // Do we need to make additional API calls to get the detailed chart data?
        if (loadingDetail === DATA_INITIALISING) {
            setLoadingDetail(DATA_LOADING)
            getReadData();
            getWrittenData();
            getTimeData();
        }

        // Toggle the displayed chart.
        setMode(((mode === VIEW_MODE.BASIC) ? VIEW_MODE.DETAIL : VIEW_MODE.BASIC));
    }

    return (
        <div className="card collapsible">
            <div className="card-header">
                <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                Average I/O over time
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse" data-bs-target="#collapseDiskCharts" aria-expanded="false" aria-controls="collapseDiskCharts" />
                <div className="btn-group float-end">
                    <button type="button" className="btn btn-xsm btn-primary float-end ms-1" onClick={toggleChart}>{((mode === VIEW_MODE.BASIC) ? 'Show Breakdown' : 'Show Summary')}</button>
                </div>
            </div>
            <div id="collapseDiskCharts" className="card-body collapse show p-0">
                <div className="row row-cols-1">
                    <div className="col p-3">
                        <h4 className='icons-chart-title'>
                            Average I/O
                            {timePeak !== null && (
                                <span className="peak">Peak Average I/O {Math.round(timePeak)}%</span>
                            )}
                        </h4>
                        <ConditionalRender if={mode === VIEW_MODE.BASIC}>
                            <ConditionalRender if={loadingBasic === DATA_LOADING}>
                                <div className="w-100 text-center text-muted mt-3">
                                    <div className="loader spinner chartSpinner">
                                    </div>
                                    <p className="mt-3">
                                        Loading data...
                                    </p>
                                </div>
                            </ConditionalRender>
                            <ConditionalRender if={loadingBasic === DATA_LOADED}>
                                {basicChart}
                            </ConditionalRender>
                        </ConditionalRender>

                        <ConditionalRender if={mode === VIEW_MODE.DETAIL}>
                            <ConditionalRender if={loadingDetail === DATA_LOADING}>
                                <div className="w-100 text-center text-muted mt-3">
                                    <div className="loader spinner chartSpinner">
                                    </div>
                                    <p className="mt-3">
                                        Loading data...
                                    </p>
                                </div>
                            </ConditionalRender>
                            <ConditionalRender if={loadingDetail === DATA_LOADED}>
                                {readWritesChart}
                                <br />
                                <br />
                                {readWrittenChart}
                                <br />
                                <br />
                                {timeChart}
                            </ConditionalRender>
                        </ConditionalRender>

                    </div>
                </div>
            </div>
        </div>
    );
}

export default DiskChart;
